import cn from 'classnames';
import { ClientEvents } from 'common/events/ClientEvents';
import { getSelectedUnitsFromLineItemByFoodType } from 'common/MealPlanUtils';
import { gt } from 'common/PatternMatchingUtilities';
import React, { useMemo } from 'react';
import { MealPlanFormat, OrderStatus, UpcomingMealPlanGroupOrderFragment } from 'src/gqlReactTypings.generated.d';
import { DishCardDisplayMode } from 'src/pages/consumer/meal-plans/checkout/types';
import { MealPlanDishCardLargeDisplay } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard/MealPlanDishCardLargeDisplay';
import { MealPlanDishCardSmallDisplay } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard/MealPlanDishCardSmallDisplay';
import { getDateCopy } from 'src/pages/consumer/meal-plans/upcoming-order/utils/getDateCopy';
import { sortOrdersByMainsAndSides } from 'src/pages/consumer/meal-plans/upcoming-order/utils/getMainsAndSidesFromGroupOrder';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { match, P } from 'ts-pattern';
import { Container, ContainerWidth } from 'web-common/src/shared/design-system/components/Container';
import { ScrollSnapCarousel } from 'web-common/src/shared/design-system/components/ScrollSnapCarousel';
import { ReactComponent as SelectDishes } from '../assets/selectDishes.svg';
import { ReactComponent as SkippedOrderCalendar } from '../assets/skippedOrderCalendar.svg';

const CONFIRMED_ORDER_STATUS = [OrderStatus.Held, OrderStatus.Pending];

const ContentForEmptyAndSkippedOrders = ({
  heading,
  subHeading,
  icon,
}: {
  heading: string;
  subHeading: string;
  icon: React.ReactNode;
}) => (
  <div className='flex flex-col items-center gap-4 px-4 text-center'>
    {icon}
    <div>
      <p className='mb-1 text-caption-700 uppercase text-eggplant'>{heading}</p>
      <p className='mb-0 text-body-400 text-eggplant-500'>{subHeading}</p>
    </div>
  </div>
);

interface OrderStatusAndDishCarouselProps {
  format: MealPlanFormat;
  groupOrder: UpcomingMealPlanGroupOrderFragment;
  numServings: number;
  unit: number;
}

export const OrderStatusAndDishCarousel: React.FC<OrderStatusAndDishCarouselProps> = ({
  format,
  groupOrder,
  numServings,
  unit,
}) => {
  const tracker = useTracker();
  const history = useHistory();
  const deliveryDate = new Date(groupOrder.deliveryDate);
  const deliveryDateCopy = getDateCopy(deliveryDate, 'short');

  const navigateToMealPlanUpcomingOrder = () => {
    tracker.track(ClientEvents.WSS_TAB_VIEW_UPCOMING, { location: 'dish-card' });
    history.push(Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER);
  };

  const { ordersWithMains, ordersWithSides } = sortOrdersByMainsAndSides(groupOrder.orders);
  const [numSelectedMains, numSelectedSides] = useMemo(
    () => [
      ordersWithMains
        .flatMap(({ lineItems }) => lineItems)
        .reduce(
          (acc, { foodItem, quantity }) =>
            acc + getSelectedUnitsFromLineItemByFoodType(foodItem.mealPlanFoodType, quantity, numServings),
          0
        ),
      ordersWithSides
        .flatMap(({ lineItems }) => lineItems)
        .reduce(
          (acc, { quantity, foodItem }) =>
            acc + getSelectedUnitsFromLineItemByFoodType(foodItem.mealPlanFoodType, quantity, numServings),
          0
        ),
    ],
    [numServings, ordersWithMains, ordersWithSides]
  );

  const mainsSelectedText = (
    <h5 className='mb-2 pl-4 text-h5-bold uppercase text-eggplant'>
      {numSelectedMains} / {unit} Mains
    </h5>
  );

  const orderSkippedContent = (
    <ContentForEmptyAndSkippedOrders
      heading='You skipped this week'
      subHeading='You’re missing out on homemade food this week.'
      icon={<SkippedOrderCalendar className='-mt-4 h-[90px] md:h-[200px]' />}
    />
  );

  const emptyOrderContent = (
    <div>
      {mainsSelectedText}
      <ContentForEmptyAndSkippedOrders
        heading='Freshly Made, Limited Supply!'
        subHeading='Select your dishes before cutoff or we’ll handpick them for you.'
        icon={<SelectDishes className='mb-2 h-[90px] md:h-[200px]' />}
      />
    </div>
  );

  const dishContent = (
    <div>
      {mainsSelectedText}
      <div className='flex items-center'>
        <ScrollSnapCarousel showSideGradients centerContent={false} showScrollButtonsOnLargeScreen addLeftMargin>
          {ordersWithMains
            .flatMap(({ lineItems }) => lineItems)
            .map(({ foodItem, quantity }) => (
              <MealPlanDishCardLargeDisplay
                displayMode={DishCardDisplayMode.Carousel}
                showShef
                foodItem={foodItem}
                numSelected={getSelectedUnitsFromLineItemByFoodType(foodItem.mealPlanFoodType, quantity, numServings)}
                key={foodItem.name}
                servingsPerMeal={numServings}
                onClick={navigateToMealPlanUpcomingOrder}
              />
            ))}
        </ScrollSnapCarousel>
      </div>
      {ordersWithSides.length > 0 && (
        <div>
          <h5 className='mb-2 pl-4 text-h5-bold uppercase text-eggplant'>
            {numSelectedSides} Side{numSelectedSides === 1 ? '' : 's'}
          </h5>
          <div className='flex items-center'>
            <ScrollSnapCarousel showSideGradients centerContent={false} addLeftMargin showScrollButtonsOnLargeScreen>
              {ordersWithSides
                .flatMap(({ lineItems }) => lineItems)
                .map(({ foodItem, quantity }) => (
                  <MealPlanDishCardSmallDisplay
                    format={format}
                    displayMode={DishCardDisplayMode.Carousel}
                    showShef
                    foodItem={foodItem}
                    numSelected={getSelectedUnitsFromLineItemByFoodType(
                      foodItem.mealPlanFoodType,
                      quantity,
                      numServings
                    )}
                    key={foodItem.name}
                    onClick={navigateToMealPlanUpcomingOrder}
                  />
                ))}
            </ScrollSnapCarousel>
          </div>
        </div>
      )}
    </div>
  );

  const status = groupOrder.orders[0].orderStatus;

  const { orderStatusText, statusCss, mainContent } = match({
    status,
    allDishesSelected: numSelectedMains === unit,
    numSelectedMains,
  })
    .with({ status: OrderStatus.Skipped }, () => ({
      orderStatusText: 'Skipped',
      statusCss: 'bg-butter-200 text-butter-600',
      mainContent: orderSkippedContent,
    }))
    .with({ status: P.when((s) => CONFIRMED_ORDER_STATUS.includes(s)), numSelectedMains: 0 }, () => ({
      orderStatusText: 'Action Required',
      statusCss: 'bg-strawberry-100 text-strawberry-500',
      mainContent: emptyOrderContent,
    }))
    .with(
      {
        status: P.when((s) => CONFIRMED_ORDER_STATUS.includes(s)),
        allDishesSelected: false,
        numSelectedMains: gt(0),
      },
      () => ({
        orderStatusText: 'Action Required',
        statusCss: 'bg-strawberry-100 text-strawberry-500',
        mainContent: dishContent,
      })
    )
    .otherwise(() => ({
      orderStatusText: 'Confirmed',
      statusCss: 'bg-mint-100 text-mint-600',
      mainContent: dishContent,
    }));

  return (
    <div className='bg-sourdough py-4'>
      <Container containerWidth={ContainerWidth.Narrow}>
        <div className='rounded-lg bg-white py-4'>
          <div className='mb-4 flex items-center justify-between px-4'>
            <p className='mb-0 rounded-md bg-rice p-2 pb-1 text-label-400 text-eggplant'>{deliveryDateCopy} delivery</p>
            <p className={cn(statusCss, 'mb-0 rounded-md p-2 pb-1 text-caption-600')}>{orderStatusText}</p>
          </div>
          {mainContent}
        </div>
      </Container>
    </div>
  );
};
